import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from '@/stores/toastStore';
import api from '@/services/api';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { useTranslation } from 'react-i18next';

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const token = searchParams.get('token');
  const { t } = useTranslation('auth');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const password = watch('password');

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setIsValidToken(false);
      toast.error('Invalid or missing reset token');
    }
  }, [token]);

  // Real-time password validation
  const validatePassword = (pwd: string) => {
    const errs: string[] = [];
    if (pwd.length < 8) errs.push('At least 8 characters');
    if (!/[A-Z]/.test(pwd)) errs.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errs.push('One lowercase letter');
    if (!/[0-9]/.test(pwd)) errs.push('One number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) errs.push('One special character');
    setPasswordErrors(errs);
  };

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      toast.error('Invalid reset token');
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        password: data.password,
      });

      setIsSuccess(true);
      toast.success(t('resetPassword.successMessage'));

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error?.message || 'Failed to reset password. Please try again.';
      toast.error(errorMessage);

      // If token is invalid or expired
      if (error.response?.status === 400 || error.response?.status === 401 || error.response?.status === 404) {
        setIsValidToken(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-purple-50/60 fixed inset-0 overflow-auto">
        <div className="w-full max-w-md my-auto">
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-8 shadow-lg text-center animate-scale-in">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6 animate-pulse">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('resetPassword.successTitle')}</h1>
            <p className="text-gray-600 mb-6">
              {t('resetPassword.successSubtitle')}
            </p>
            <div className="flex justify-center">
              <div className="animate-spin w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!isValidToken) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-purple-50/60 fixed inset-0 overflow-auto">
        <div className="w-full max-w-md my-auto">
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-8 shadow-lg text-center animate-scale-in">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('resetPassword.invalidTokenTitle')}</h1>
            <p className="text-gray-600 mb-8">
              {t('resetPassword.invalidTokenMessage')}
            </p>
            <div className="space-y-3">
              <Link to="/forgot-password">
                <Button variant="primary" fullWidth>
                  {t('resetPassword.requestNewLink')}
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="secondary" fullWidth>
                  {t('resetPassword.backToLogin')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-purple-50/60 fixed inset-0 overflow-auto">
      <div className="w-full max-w-md my-auto">
        {/* Back to Login Link */}
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors"
        >
          <ArrowRight className="w-5 h-5 rotate-180" />
          <span>{t('resetPassword.backToLogin')}</span>
        </Link>

        {/* Title */}
        <div className="text-center mb-8 animate-slide-down">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('resetPassword.title')}</h1>
          <p className="text-gray-600">{t('resetPassword.subtitle')}</p>
        </div>

        {/* Form */}
        <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-8 shadow-lg animate-slide-up">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* New Password */}
            <div>
              <Input
                {...register('password', {
                  onChange: (e) => validatePassword(e.target.value),
                })}
                type="password"
                label={t('resetPassword.passwordLabel')}
                placeholder={t('resetPassword.passwordPlaceholder')}
                error={errors.password?.message}
                icon={<Lock className="w-5 h-5" />}
                autoComplete="new-password"
                disabled={isLoading}
              />
              {password && (
                <div className="mt-3 space-y-2">
                  {passwordErrors.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>{t('resetPassword.strongPassword')}</span>
                    </div>
                  ) : (
                    <div className="text-sm">
                      <p className="font-medium text-gray-700 mb-2">{t('resetPassword.passwordRequirements')}</p>
                      <ul className="space-y-1">
                        {passwordErrors.map((err, i) => (
                          <li key={i} className="flex items-center gap-2 text-gray-600">
                            <span className="text-red-600">✗</span>
                            <span>{err}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <Input
              {...register('confirmPassword')}
              type="password"
              label={t('resetPassword.confirmPasswordLabel')}
              placeholder={t('resetPassword.confirmPasswordPlaceholder')}
              error={errors.confirmPassword?.message}
              icon={<Lock className="w-5 h-5" />}
              autoComplete="new-password"
              disabled={isLoading}
            />

            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={isLoading}
              className="group"
            >
              {!isLoading && <CheckCircle2 className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />}
              {t('resetPassword.submitButton')}
            </Button>
          </form>
        </div>

        {/* Security Info */}
        <div className="mt-6 bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-4 shadow-sm animate-slide-up delay-100">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="mb-2">
                <span className="font-semibold text-gray-900">{t('resetPassword.passwordTipsTitle')}</span>
              </p>
              <ul className="space-y-1 list-disc list-inside">
                <li>{t('resetPassword.tip1')}</li>
                <li>{t('resetPassword.tip2')}</li>
                <li>{t('resetPassword.tip3')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
